
import React, { useState } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

interface FloorPlan {
  floorImage: string;
  size: number;
  floorNumber: number;
  _id: string;
}

interface FloorPlansProps {
  projectData: {
    floorPlans: FloorPlan[];
  };
}

const FloorPlans: React.FC<FloorPlansProps> = ({ projectData }) => {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange =
    (panel: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  // Grouping floor plans by floorNumber
  const groupedFloorPlans = projectData.floorPlans.reduce((acc, floorPlan) => {
    if (!acc[floorPlan.floorNumber]) acc[floorPlan.floorNumber] = [];
    acc[floorPlan.floorNumber].push(floorPlan);
    return acc;
  }, {} as Record<number, FloorPlan[]>);

  // Slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 2000,
    pauseOnHover: true,
    fade: true,
    centerMode: true,
    adaptiveHeight: true,
  };

  return (
    <Box
      sx={{
        padding: "20px",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        marginBottom: "20px",
      }}
    >
      <Typography variant="h5" sx={{ color: "#333", fontWeight: 600 }}>
        Floor Plans
      </Typography>
      {Object.entries(groupedFloorPlans).map(
        ([floorNumber, floorPlans], index) => (
          <Accordion
            key={floorNumber}
            expanded={expanded === index}
            onChange={handleChange(index)}
            sx={{ marginTop: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1">{floorNumber} BHK</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="caption">
                    {floorPlans.length} Floor Plan
                    {floorPlans.length > 1 ? "s" : ""}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  maxWidth: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {floorPlans.length > 1 ? (
                  <Box sx={{ maxWidth: "400px", margin: "auto" }}>
                    {" "}
                    {/* Add style wrapper here */}
                    <Slider
                      dots={true}
                      infinite={true}
                      speed={500}
                      slidesToShow={1}
                      slidesToScroll={1}
                      arrows={true}
                      autoplay={true}
                      autoplaySpeed={1500}
                      pauseOnHover={true}
                      adaptiveHeight={true}
                    >
                      {floorPlans.map((floorPlan) => (
                        <Box key={floorPlan._id} sx={{ textAlign: "center" }}>
                          <img
                            src={`${
                              process.env.NEXT_PUBLIC_STORAGE_DN_URL ||
                              "https://dprstorage.b-cdn.net"
                            }${floorPlan.floorImage}`}
                            alt={`Floor ${floorPlan.floorNumber} Plan`}
                            style={{
                              width: "100%",
                              maxWidth: "400px",
                              height: "300px",
                              objectFit: "contain",
                              borderRadius: "8px",
                              display: "block",
                              margin: "auto",
                            }}
                          />
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ marginTop: "8px" }}
                          >
                            Size:{" "}
                            {floorPlan.size > 6
                              ? `${floorPlan.size} SQFT`
                              : `${floorPlan.size} BHK`}
                          </Typography>
                        </Box>
                      ))}
                    </Slider>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center" }}>
                    <img
                      src={`${
                        process.env.NEXT_PUBLIC_STORAGE_DN_URL ||
                        "https://dprstorage.b-cdn.net"
                      }${floorPlans[0].floorImage}`}
                      alt={`Floor ${floorPlans[0].floorNumber} Plan`}
                      style={{
                        width: "100%",
                        maxWidth: "400px",
                        height: "300px",
                        objectFit: "contain",
                        borderRadius: "8px",
                        display: "block",
                        margin: "auto",
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ marginTop: "8px" }}
                    >
                      Size:{" "}
                      {floorPlans[0].size > 6
                        ? `${floorPlans[0].size} SQFT`
                        : `${floorPlans[0].size} BHK`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )
      )}
    </Box>
  );
};

export default FloorPlans;

